import { useEffect, useState } from 'react';
import type { ComponentContentSchema, ComponentSlot, SlotGroupDefinition } from '@vibesites/runtime';
import {
  Slot,
  useSlotGroup,
  useVibesites,
  usePropertyAddress,
  usePropertyAddressUrl,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import * as Dialog from '@radix-ui/react-dialog';
import { dialogMotion } from '../utils/motion';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, formatPhone, telHref } from '../utils/format';
import { useRawProperty, useScrollReveal, useScrollState } from '../utils/hooks';
import { PATHS, useSiteFlags } from '../utils/site';
import { SocialLinks } from '../utils/social';

const logoDef: SlotGroupDefinition = {
  label: 'Logo',
  description: 'Shown in white on the property colour. A full-colour logo is flattened to white automatically.',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo'],
    },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    short_label: { label: 'Button Text (phone)', type: 'text', defaultValue: 'Book a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const applyLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const portalLinkDef: SlotGroupDefinition = {
  label: 'Resident Portal Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Portal' },
    url: { label: 'URL', type: 'url', defaultField: 'property_resident_portal_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'resident_portal_clicked' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  description: 'The number shows on desktop; phones and tablets show the link text instead. Formatted as (555) 555-5555 automatically.',
  fields: {
    number: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    label: { label: 'Link Text (phone & tablet)', type: 'text', defaultValue: 'Call Us' },
  },
};

interface NavLink {
  label: string;
  href: string;
  alias: string;
}

interface NavGroup {
  label: string;
  links: NavLink[];
}

const utilityHit =
  'max-[767px]:[margin:-13px_0] max-[767px]:[padding:13px_0] max-[360px]:[margin:-14px_0] max-[360px]:[padding:14px_0] min-[768px]:max-[1099px]:[margin:-12px_0] min-[768px]:max-[1099px]:[padding:12px_0]';
const utilityLink = `no-underline max-[1099px]:inline-block max-[1099px]:whitespace-nowrap ${utilityHit}`;
const navLink = `relative inline-flex items-center gap-1.5 whitespace-nowrap py-2.5 text-[15px] font-medium no-underline after:absolute after:bottom-1 after:left-0 after:right-0 after:h-[1.5px] after:origin-left after:bg-[--q-accent-on-primary] after:content-[''] after:[transition:transform_var(--q-motion-normal)_var(--q-ease-out)] hover:after:[transform:scaleX(1)] focus-visible:outline-offset-[3px] focus-visible:[outline:2px_solid_var(--q-accent-on-primary)]`;
const navLinkLine = (on: boolean) => (on ? 'after:[transform:scaleX(1)]' : 'after:[transform:scaleX(0)]');
const navMenu =
  'absolute left-[-20px] top-full flex w-[260px] flex-col rounded-md border border-[--q-border] bg-[--q-paper] py-2.5 text-[--q-ink] [box-shadow:var(--q-shadow-md)] [transition:opacity_var(--q-motion-normal)_var(--q-ease-out),transform_var(--q-motion-normal)_var(--q-ease-out),visibility_0s_linear_var(--q-motion-normal)] group-hover/navitem:visible group-hover/navitem:opacity-100 group-hover/navitem:[transform:none] group-hover/navitem:[transition-delay:0s] group-focus-within/navitem:visible group-focus-within/navitem:opacity-100 group-focus-within/navitem:[transform:none] group-focus-within/navitem:[transition-delay:0s] motion-reduce:[transform:none]';
const drawerGroup = 'pb-1.5 pt-5 text-[11px] font-medium uppercase tracking-[--q-tracking-label] text-[--q-secondary]';
const drawerLink = 'py-2 font-medium underline underline-offset-[3px]';

export function Header() {
  useScrollReveal();
  const { page } = useVibesites();
  const p = useRawProperty();
  const flags = useSiteFlags();
  const directionsUrl = usePropertyAddressUrl();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const { scrolled, down } = useScrollState();
  const logo = useSlotGroup('header.logo', logoDef);
  const tour = useSlotGroup('header.tour_cta', tourCtaDef);
  const apply = useSlotGroup('header.apply_link', applyLinkDef);
  const portal = useSlotGroup('header.portal_link', portalLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);

  const name = clean(p.property_name) || '';
  const company = clean(p.property_company_name) || '';
  const address = clean(usePropertyAddress()) || '';
  const phone = formatPhone(phoneLink.number) || flags.phone;
  const tel = telHref(phone);
  const applyHref = apply.url || flags.applyUrl;
  const tourHref = tour.url || PATHS.selfTour;
  const tourLabel = tour.label || 'Book Self-Guided Tour';
  const isHome = !page?.path || page.path === '/';

  // the property's own mono logo, not the company's copied into the field; else its primary logo, flattened
  const companyMono = clean(p.property_company_mono_white_logo);
  const ownMono = logo.image && logo.image !== companyMono ? logo.image : null;
  const primaryLogo = clean(p.property_primary_logo);
  const logoSrc = ownMono || primaryLogo;
  const logoDerived = !!logoSrc && logoSrc === primaryLogo && logoSrc !== clean(p.property_mono_white_logo);
  const companyWord = company.split(/\s+/)[0];
  const showBy = !!company && !(companyWord && new RegExp(companyWord, 'i').test(name));

  const link = (alias: string, label: string, href: string): NavLink | null => (flags.shows(alias) ? { alias, label, href } : null);
  const groups: Array<NavGroup | NavLink> = [
    { label: 'Floor Plans', links: [link('floor-plans', 'Floor Plans', PATHS.floorPlans), link('interactive-map', 'Interactive Map', PATHS.interactiveMap)].filter(Boolean) as NavLink[] },
    {
      label: 'Community',
      links: [
        link('amenities', 'Amenities', PATHS.amenities),
        link('gallery', 'Gallery', PATHS.gallery),
        link('virtual-tours', 'Virtual Tours', PATHS.virtualTours),
        link('pet-policy', 'Pet Policy', PATHS.petPolicy),
      ].filter(Boolean) as NavLink[],
    },
    ...([link('neighborhood', 'Neighborhood', PATHS.neighborhood), link('reviews', 'Reviews', PATHS.reviews), link('faqs', 'FAQs', PATHS.faqs), link('contact', 'Contact', PATHS.contact)].filter(Boolean) as NavLink[]),
  ];
  const isCurrent = (href: string) => page?.path === href;

  useEffect(() => {
    if (!openGroup) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element)?.closest?.('.nav__item--group')) setOpenGroup(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openGroup]);

  const solid = !isHome || scrolled;
  const headerClass = [
    'site-header',
    isHome ? 'mb-[calc(-1*(var(--header-utility)+var(--header-main)))]' : '',
    scrolled && down ? '[transform:translateY(calc(-1*var(--header-utility)))]' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <Slot slot={'SpecialOfferBanner' as ComponentSlot} />
      <header className={headerClass}>
        <div className="flex h-[--header-utility] items-center justify-between border-b border-b-[color-mix(in_srgb,var(--vibe-color-text-on-primary)_18%,transparent)] bg-primary px-[--gutter] text-[13px] font-medium max-[767px]:gap-4 max-[767px]:text-[12px] max-[360px]:gap-2.5 max-[360px]:text-[11px] min-[768px]:max-[1099px]:gap-[22px]">
          <div className="flex items-center gap-4 max-[1099px]:contents">
            {address && <span className="font-normal opacity-[.85] max-[1099px]:hidden">{address}</span>}
            <a className={`underline underline-offset-[3px] max-[1099px]:hidden ${utilityHit}`} href={directionsUrl} target="_blank" rel="noopener">Get Directions</a>
            <SocialLinks
              className={[
                'ml-2 border-l border-l-[color-mix(in_srgb,var(--vibe-color-text-on-primary)_25%,transparent)] pl-3',
                'max-[767px]:[&.social]:hidden max-[360px]:ml-1 max-[360px]:pl-1.5',
                'min-[768px]:max-[1099px]:order-[-1] min-[768px]:max-[1099px]:[margin:0_auto_0_0] min-[768px]:max-[1099px]:pl-2.5',
                'min-[768px]:max-[1099px]:[&.social_a]:h-11 min-[768px]:max-[1099px]:[&.social_a]:w-11 min-[768px]:max-[1099px]:[&.social_a]:[margin:-6px_-6px]',
              ].join(' ')}
            />
          </div>
          <div className="flex items-center gap-7 max-[1099px]:contents">
            <EditableSlotGroup
              groupPath="header.phone_link"
              definition={phoneLinkDef}
              className="max-[767px]:order-[-1] max-[767px]:mr-auto min-[768px]:max-[1099px]:order-[-2]"
            >
              {tel && (
                <a className={`dniphonehref ${utilityLink} max-[1099px]:font-medium`} href={tel}>
                  <span className="dniphone max-[1099px]:hidden">{phone}</span>
                  <span className="hidden max-[1099px]:inline">{phoneLink.label || 'Call Us'}</span>
                </a>
              )}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.apply_link" definition={applyLinkDef}>
              {applyHref && (
                <a className={utilityLink} href={applyHref} target={apply._target || '_self'} data-vibesite-event={apply.vibesite_event || undefined}>
                  {apply.label || 'Apply Now'}
                </a>
              )}
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.portal_link" definition={portalLinkDef}>
              {portal.url && (
                <a className={utilityLink} href={portal.url} target={portal._target || '_self'} data-vibesite-event={portal.vibesite_event || undefined}>
                  {portal.label || 'Resident Portal'}
                </a>
              )}
            </EditableSlotGroup>
          </div>
        </div>

        <div
          className={`flex h-[--header-main] items-center justify-between gap-6 px-[--gutter] max-[767px]:gap-3 [transition:background-color_400ms_ease] ${
            solid ? 'bg-primary text-primary-foreground' : 'bg-transparent text-[--q-on-image]'
          }`}
        >
          <EditableSlotGroup groupPath="header.logo" definition={logoDef} className="max-[767px]:min-w-0 max-[767px]:flex-auto">
            <a
              className="flex h-12 w-[246px] shrink-0 items-center no-underline max-[1199px]:h-auto max-[1199px]:min-h-12 max-[1199px]:w-[220px] max-[767px]:w-auto max-[767px]:min-w-0 max-[767px]:flex-auto max-[767px]:min-h-10"
              href="/"
              aria-label="Home"
            >
              {logoSrc ? (
                <VibesitesImage
                  url={logoSrc}
                  alt={company ? `${name} by ${company}` : name}
                  sizes="260px"
                  priority
                  className={`h-11 w-auto max-w-[260px] object-contain max-[767px]:h-8 max-[767px]:max-w-[min(180px,46vw)] max-[360px]:max-w-[42vw]${logoDerived ? ' [filter:brightness(0)_invert(1)]' : ''}`}
                />
              ) : (
                <span className="flex flex-col gap-[3px] max-[767px]:min-w-0">
                  <span className="line-clamp-2 font-heading text-[24px] leading-[26px] tracking-[-0.01em] max-[1199px]:whitespace-normal max-[1199px]:text-[20px] max-[1199px]:leading-6 max-[767px]:text-[19px] max-[767px]:leading-[22px]">
                    {name}
                  </span>
                  {showBy && <span className="text-[10px] font-medium uppercase leading-3 tracking-[.18em] opacity-[.85]">by {company}</span>}
                </span>
              )}
            </a>
          </EditableSlotGroup>

          <nav className="flex items-center gap-8 max-[1399px]:gap-[22px] max-[1099px]:hidden" aria-label="Primary">
            {groups.map((g) => {
              if (!('links' in g)) {
                return (
                  <a key={g.href} className={`${navLink} ${navLinkLine(isCurrent(g.href))}`} href={g.href}>
                    {g.label}
                  </a>
                );
              }
              if (!g.links.length) return null;
              if (g.links.length === 1) {
                const only = g.links[0];
                return (
                  <a key={only.href} className={`${navLink} ${navLinkLine(isCurrent(only.href))}`} href={only.href}>
                    {only.label}
                  </a>
                );
              }
              const open = openGroup === g.label;
              const current = g.links.some((l) => isCurrent(l.href));
              return (
                <div key={g.label} className="nav__item--group group/navitem relative">
                  <button
                    className={`${navLink} ${navLinkLine(open || current)} group-hover/navitem:after:[transform:scaleX(1)]`}
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenGroup(open ? null : g.label)}
                  >
                    {g.label}{' '}
                    <span
                      className={`text-[14px] leading-[14px] opacity-90 [transition:transform_var(--q-motion-normal)_var(--q-ease-out)] group-hover/navitem:[transform:rotate(180deg)]${open ? ' [transform:rotate(180deg)]' : ''}`}
                      aria-hidden="true"
                    >
                      ▾
                    </span>
                  </button>
                  <div className={`${navMenu} ${open ? 'visible opacity-100 [transform:none] [transition-delay:0s]' : 'invisible opacity-0 [transform:translateY(-6px)]'}`}>
                    {g.links.map((l) => (
                      <a key={l.href} className="px-5 py-2.5 text-[15px] font-medium no-underline hover:bg-[--q-surface]" href={l.href}>{l.label}</a>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-3.5 max-[767px]:shrink-0 max-[767px]:[&>*]:shrink-0 max-[360px]:gap-2">
            <EditableSlotGroup groupPath="header.tour_cta" definition={tourCtaDef}>
              <a
                className="btn btn-on-image [&.btn]:min-h-11 [&.btn]:text-[15px] [&.btn]:[padding:0_20px] max-[767px]:[&.btn]:w-auto max-[767px]:[&.btn]:text-[13px] max-[767px]:[&.btn]:[padding:0_14px] max-[360px]:[&.btn]:text-[12px] max-[360px]:[&.btn]:[padding:0_10px]"
                href={tourHref}
                target={tour._target || '_self'}
                data-vibesite-event={tour.vibesite_event || undefined}
              >
                <span className="max-[1399px]:hidden">{tourLabel}</span>
                <span className="hidden max-[1399px]:inline">{tour.short_label || 'Book a Tour'}</span>
              </a>
            </EditableSlotGroup>
            <button
              className="hidden h-11 w-11 flex-col items-center justify-center gap-[5px] max-[1099px]:inline-flex"
              type="button"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <span className="block h-0.5 w-[22px] bg-current"></span><span className="block h-0.5 w-[22px] bg-current"></span><span className="block h-0.5 w-[22px] bg-current"></span>
            </button>
          </div>
        </div>
      </header>

      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className={`fixed inset-0 z-[60] bg-[color-mix(in_srgb,var(--q-ink)_50%,transparent)] ${dialogMotion.fade}`} />
          <Dialog.Content
            aria-describedby={undefined}
            className={`fixed bottom-0 right-0 top-0 z-[60] flex w-[min(360px,88vw)] flex-col gap-6 overflow-y-auto bg-[--q-paper] px-6 pb-8 pt-6 outline-none ${dialogMotion.slide}`}
          >
            <Dialog.Title className="sr-only">Menu</Dialog.Title>
            <Dialog.Close className="h-11 w-11 self-end text-[28px]" aria-label="Close menu">×</Dialog.Close>
            <nav
              className="flex flex-col [&_a]:border-b [&_a]:border-b-[--q-border] [&_a]:py-3 [&_a]:font-heading [&_a]:text-[22px] [&_a]:no-underline"
              aria-label="Mobile"
            >
              <div className={drawerGroup}>Floor Plans</div>
              <a href={PATHS.floorPlans}>All Floor Plans</a>
              {flags.shows('interactive-map') && <a href={PATHS.interactiveMap}>Interactive Map</a>}
              <div className={drawerGroup}>Community</div>
              {flags.shows('amenities') && <a href={PATHS.amenities}>Amenities</a>}
              {flags.shows('gallery') && <a href={PATHS.gallery}>Gallery</a>}
              {flags.shows('virtual-tours') && <a href={PATHS.virtualTours}>Virtual Tours</a>}
              <div className={drawerGroup}>More</div>
              {flags.shows('neighborhood') && <a href={PATHS.neighborhood}>Neighborhood</a>}
              {flags.shows('reviews') && <a href={PATHS.reviews}>Reviews</a>}
              {flags.shows('faqs') && <a href={PATHS.faqs}>FAQs</a>}
              {flags.shows('pet-policy') && <a href={PATHS.petPolicy}>Pet Policy</a>}
              <a href={PATHS.contact}>Contact</a>
            </nav>
            <div className="flex flex-col gap-2.5">
              <a className="btn btn-primary" href={tourHref} target={tour._target || '_self'} data-vibesite-event={tour.vibesite_event || undefined}>
                {tourLabel}
              </a>
              {applyHref && (
                <a className="btn btn-secondary" href={applyHref} data-vibesite-event={apply.vibesite_event || undefined}>
                  {apply.label || 'Apply Now'}
                </a>
              )}
              {portal.url && (
                <a className={drawerLink} href={portal.url} data-vibesite-event={portal.vibesite_event || undefined}>
                  {portal.label || 'Resident Portal'}
                </a>
              )}
              {tel && <a className={`dniphone dniphonehref ${drawerLink}`} href={tel}>{phone}</a>}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {},
  slotGroups: {
    'header.phone_link': phoneLinkDef,
    'header.logo': logoDef,
    'header.tour_cta': tourCtaDef,
    'header.apply_link': applyLinkDef,
    'header.portal_link': portalLinkDef,
  },
};

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Header;
